import type { Dictionary, FiltersMessages } from '../models'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'

import {
  ActiveFilterEnum,
  AuditTypeEnum,
  CompareFilterEnum,
  EnabledFilterEnum,
  FeatureIdEnum,
  FilterIdEnum,
  LocaleEnum,
} from '../enums'

import { AuditObjectTypeEnum } from '../enums/audit-object-type.enum'

import { APP_MESSAGES } from './app.messages'

const APP_MESSAGES_EN = APP_MESSAGES[LocaleEnum.ENGLISH].app
const FILTERS_MESSAGES_EN: FiltersMessages = {
  filters: 'Filters',
  resetAll: 'Reset all',
  clearFilters: 'Clear filters',
  names: {
    [FilterIdEnum.DATE]: 'Date',
    [FilterIdEnum.REALTIME]: 'Realtime',
    [FilterIdEnum.DEVICE]: 'Device',
    [FilterIdEnum.LANG]: 'Language',
    [FilterIdEnum.CATALOG]: 'Catalog',
    [FilterIdEnum.STORE]: 'Store',
    [FilterIdEnum.COMPARE]: 'Compare with',
    [FilterIdEnum.ENABLED]: 'Status',
    [FilterIdEnum.ACTIVE]: 'Active',
    [FilterIdEnum.AUDIT_OBJECT_TYPE]: 'Tool',
    [FilterIdEnum.AUDIT_ACTION]: 'Action',
    [FilterIdEnum.UPDATED_ON]: 'Updated on',
    [FilterIdEnum.SCHEDULED]: 'Scheduled',
    [FilterIdEnum.ENDPOINT]: 'Endpoint',
  },
  values: {
    all: {
      default: 'All by default',
      value: 'All',
      select: 'Select all',
    },
    none: 'None',
    [FilterIdEnum.DATE]: {
      today: {
        label: 'Today',
      },
      lastSevenDays: {
        label: 'Last 7 days',
      },
      lastThirtyDays: {
        label: 'Last 30 days',
      },
      custom: {
        label: 'Custom date',
      },
      from: 'from {date}',
      to: 'to {date}',
    },
    [FilterIdEnum.REALTIME]: {
      hours: {
        short: 'Last {hours} hrs',
        long: 'Last {hours} hours',
      },
      minutes: {
        short: 'Last {minutes} mins',
        long: 'Last {minutes} minutes',
      },
    },
    [FilterIdEnum.DEVICE]: {
      desktop: 'Desktop',
      mobile: 'Mobile',
      mobileapp: 'App',
      mobileappandroid: 'Android App',
      mobileappios: 'iOS App',
      tablet: 'Tablet',
    },
    [FilterIdEnum.LANG]: {
      ar: 'Arabian',
      ar_SA: 'Arabian (Saudi Arabia)',
      ar_LB: 'Arabian (Lebanon)',
      ar_AE: 'Arabian (United Arab Emirates)',
      ar_MA: 'Arabian (Morocco)',
      bg: 'Bulgarian',
      ca: 'Catalan',
      cs: 'Czech',
      da: 'Danish',
      de: 'German',
      de_DE: 'German (Germany)',
      de_AT: 'German (Austria)',
      el: 'Greek',
      en: 'English',
      en_GB: 'English (United Kingdom)',
      en_US: 'English (United States of America)',
      es: 'Spanish',
      es_ES: 'Spanish (Spain)',
      es_MX: 'Spanish (Mexico)',
      es_US: 'Spanish (United States of America)',
      et: 'Estonian',
      eu: 'Basque',
      fi: 'Finnish',
      fr: 'French',
      fr_FR: 'French (France)',
      fr_BE: 'French (Belgium)',
      gl: 'Galician',
      he: 'Hebrew',
      hr: 'Croatian',
      hu: 'Hungarian',
      id: 'Indonesian',
      it: 'Italian',
      ja: 'Japanese',
      ko: 'Korean',
      lt: 'Lithuanian',
      lv: 'Latvian',
      ms: 'Malay',
      nl: 'Dutch',
      nl_NL: 'Dutch (Netherlands)',
      nl_BE: 'Dutch (Belgium)',
      no: 'Norwegian',
      pl: 'Polish',
      pt: 'Portuguese',
      pt_BR: 'Portuguese (Brazil)',
      pt_PT: 'Portuguese (Portugal)',
      ro: 'Romanian',
      ru: 'Russian',
      sk: 'Slovak',
      sl: 'Slovenian',
      sq: 'Albanian',
      sr: 'Serbian',
      sv: 'Swedish',
      th: 'Thai',
      tr: 'Turkish',
      uk: 'Ukrainian',
      vi: 'Vietnamese',
      zh: 'Traditional Chinese',
      zh_CN: 'Traditional Chinese (China)',
      zh_TW: 'Traditional Chinese (Taiwan)',
    },
    [FilterIdEnum.STORE]: {
      AD: 'Andorra',
      AE: 'United Arab Emirates',
      AL: 'Albania',
      AM: 'Armenia',
      AT: 'Austria',
      AU: 'Australia',
      AW: 'Aruba',
      AZ: 'Azerbaijan',
      BA: 'Bosnia and Herzegovina',
      BG: 'Bulgaria',
      BE: 'Belgium',
      BH: 'Bahrain',
      BR: 'Brazil',
      BY: 'Belarus',
      CA: 'Canada',
      CH: 'Switzerland',
      CL: 'Chile',
      CN: 'China',
      CO: 'Colombia',
      CR: 'Costa Rica',
      CY: 'Cyprus',
      CZ: 'Czech Republic',
      DE: 'Germany',
      DK: 'Denmark',
      DO: 'Dominican Republic',
      DZ: 'Algeria',
      EC: 'Ecuador',
      EE: 'Estonia',
      EG: 'Egypt',
      ES: 'Spain',
      FI: 'Finland',
      FR: 'France',
      GB: 'United Kingdom',
      GE: 'Georgia',
      GR: 'Greece',
      GT: 'Guatemala',
      HK: 'Hong Kong',
      HN: 'Honduras',
      HR: 'Croatia',
      HU: 'Hungary',
      IC: 'Canary Islands',
      ID: 'Indonesia',
      IE: 'Ireland',
      IL: 'Israel',
      IN: 'India',
      IQ: 'Iraq',
      IR: 'Iran',
      IS: 'Iceland',
      IT: 'Italy',
      JO: 'Jordan',
      JP: 'Japan',
      KR: 'South Korea',
      KW: 'Kuwait',
      KZ: 'Kazakhstan',
      LB: 'Lebanon',
      LI: 'Liechtenstein',
      LK: 'Sri Lanka',
      LT: 'Lithuania',
      LU: 'Luxembourg',
      LV: 'Latvia',
      LY: 'Libya',
      MA: 'Morocco',
      MC: 'Monaco',
      ME: 'Montenegro',
      MK: 'Macedonia',
      MO: 'Macao',
      MQ: 'Martinique',
      MT: 'Malta',
      MX: 'Mexico',
      MY: 'Malaysia',
      NI: 'Nicaragua',
      NL: 'Netherlands',
      NO: 'Norway',
      NZ: 'New Zealand',
      OM: 'Oman',
      PA: 'Panama',
      PH: 'Philippines',
      PK: 'Pakistan',
      PL: 'Poland',
      PR: 'Puerto Rico',
      PT: 'Portugal',
      QA: 'Qatar',
      RO: 'Romania',
      RS: 'Serbia',
      RU: 'Russia',
      SA: 'Saudi Arabia',
      SE: 'Sweden',
      SG: 'Singapore',
      SI: 'Slovenia',
      SK: 'Slovakia',
      SY: 'Syrian Arab Republic',
      SV: 'El Salvador',
      TH: 'Thailand',
      TN: 'Tunisia',
      TR: 'Turkey',
      TW: 'Taiwan',
      UA: 'Ukraine',
      US: 'United States of America',
      VE: 'Venezuela',
      VN: 'Vietnam',
      WW: 'Worldwide',
      XK: 'Kosovo',
      ZA: 'South Africa',
    },
    [FilterIdEnum.CATALOG]: {},
    [FilterIdEnum.COMPARE]: {
      [CompareFilterEnum.PREVIOUS_PERIOD]: 'Previous period',
      [CompareFilterEnum.YEAR_OVER_YEAR]: 'Year over year',
    },
    [FilterIdEnum.ACTIVE]: {
      [ActiveFilterEnum.ACTIVE]: 'Active',
      [ActiveFilterEnum.INACTIVE]: 'Inactive',
    },
    [FilterIdEnum.ENABLED]: {
      [EnabledFilterEnum.ENABLED]: 'On',
      [EnabledFilterEnum.DISABLED]: 'Off',
    },
    [FilterIdEnum.AUDIT_OBJECT_TYPE]: {
      [AuditObjectTypeEnum.EQUALIZE]: APP_MESSAGES_EN.features[FeatureIdEnum.EQUALIZE],
      [AuditObjectTypeEnum.BLACKLIST]: APP_MESSAGES_EN.features[FeatureIdEnum.BLACKLIST],
      [AuditObjectTypeEnum.PROMOTION]: APP_MESSAGES_EN.features[FeatureIdEnum.PROMOTION],
      [AuditObjectTypeEnum.REDIRECTION]: APP_MESSAGES_EN.features[FeatureIdEnum.REDIRECTION],
      [AuditObjectTypeEnum.SYNONYM]: APP_MESSAGES_EN.features[FeatureIdEnum.SYNONYM],
      [AuditObjectTypeEnum.BROWSE_CONFIGURATION]:
        APP_MESSAGES_EN.features[FeatureIdEnum.CONFIGURATION][DomainEnum.BROWSE],
      [AuditObjectTypeEnum.SEARCH_CONFIGURATION]:
        APP_MESSAGES_EN.features[FeatureIdEnum.CONFIGURATION][DomainEnum.SEARCH],
      [AuditObjectTypeEnum.RELATED_TAGS]: APP_MESSAGES_EN.features[FeatureIdEnum.RELATED_TAGS],
      [AuditObjectTypeEnum.NEXT_QUERIES]: APP_MESSAGES_EN.features[FeatureIdEnum.NEXT_QUERIES],
      [AuditObjectTypeEnum.EXPLAIN]: APP_MESSAGES_EN.features[FeatureIdEnum.EXPLAIN],
      [AuditTypeEnum.CONFIG]: APP_MESSAGES_EN.features[FeatureIdEnum.INSTANCE_MANAGEMENT],
      [AuditObjectTypeEnum.CLIENT_CONFIG]: APP_MESSAGES_EN.features[FeatureIdEnum.CLIENT_CONFIG],
      [AuditObjectTypeEnum.ENDPOINT_CONFIG]:
        APP_MESSAGES_EN.features[FeatureIdEnum.ENDPOINT_CONFIG],
      [AuditObjectTypeEnum.FEATURE_CONFIG]: APP_MESSAGES_EN.features[FeatureIdEnum.FEATURE_CONFIG],
      [AuditObjectTypeEnum.INDEX_CLIENT]: APP_MESSAGES_EN.features[FeatureIdEnum.INDEX_CLIENT],
      [AuditObjectTypeEnum.INDEX_FEED_CONFIGURATION]:
        APP_MESSAGES_EN.features[FeatureIdEnum.INDEX_FEED_CONFIGURATION],
      [AuditObjectTypeEnum.INDEX_SCHEMA_CONFIGURATION]:
        APP_MESSAGES_EN.features[FeatureIdEnum.INDEX_SCHEMA_CONFIGURATION],
    },
  },
  customDate: {
    title: 'Select Date Range',
    startDate: 'From',
    endDate: 'To',
    error: 'A date range must be selected.',
  },
  errors: {
    [FilterIdEnum.DATE]: {
      minDate: 'Date should not be before {date}',
      maxDate: 'Date should not be after {date}',
      maxPeriod: 'The difference between dates should not be more than {period} days',
      required: 'Date is required',
    },
  },
  placeholder: {
    single: 'Select a value',
    multiple: 'Select values',
  },
  search: 'Search',
  noResults: 'No results',
  useValue: 'Use "{value}"',
}

const APP_MESSAGES_ES = APP_MESSAGES[LocaleEnum.SPANISH].app
const FILTERS_MESSAGES_ES: FiltersMessages = {
  filters: 'Filtros',
  resetAll: '',
  clearFilters: 'Limpiar filtros',
  names: {
    [FilterIdEnum.DATE]: 'Fecha',
    [FilterIdEnum.REALTIME]: 'Tiempo real',
    [FilterIdEnum.DEVICE]: 'Dispositivo',
    [FilterIdEnum.LANG]: 'Idioma',
    [FilterIdEnum.CATALOG]: 'Catálogo',
    [FilterIdEnum.STORE]: 'Tienda',
    [FilterIdEnum.COMPARE]: 'Comparar con',
    [FilterIdEnum.ACTIVE]: 'Activo',
    [FilterIdEnum.ENABLED]: 'Estado',
    [FilterIdEnum.AUDIT_OBJECT_TYPE]: 'Herramienta',
    [FilterIdEnum.AUDIT_ACTION]: 'Acción',
    [FilterIdEnum.UPDATED_ON]: '',
    [FilterIdEnum.SCHEDULED]: '',
    [FilterIdEnum.ENDPOINT]: '',
  },
  values: {
    all: {
      default: 'Todos por defecto',
      value: 'Todos',
      select: 'Seleccionar todos',
    },
    none: 'Ninguno',
    [FilterIdEnum.DATE]: {
      today: {
        label: 'Hoy',
      },
      lastSevenDays: {
        label: 'Últimos 7 días',
      },
      lastThirtyDays: {
        label: 'Últimos 30 días',
      },
      custom: {
        label: 'Personalizada',
      },
      from: 'desde {date}',
      to: 'hasta {date}',
    },
    [FilterIdEnum.REALTIME]: {
      hours: {
        short: 'Últimas {hours} h',
        long: 'Últimas {hours} horas',
      },
      minutes: {
        short: 'Últimos {minutes} mins',
        long: 'Últimos {minutes} minutos',
      },
    },
    [FilterIdEnum.DEVICE]: {
      desktop: 'Escritorio',
      mobile: 'Móvil',
      mobileapp: 'App',
      mobileappandroid: 'App Android',
      mobileappios: 'App iOS',
      tablet: 'Tablet',
    },
    [FilterIdEnum.LANG]: {
      ar: 'Árabe',
      ar_SA: 'Árabe (Arabia Saudita)',
      ar_LB: 'Árabe (Líbano)',
      ar_AE: 'Árabe (Emiratos Árabes Unidos)',
      ar_MA: 'Árabe (Marruecos)',
      bg: 'Búlgaro',
      ca: 'Catalán',
      cs: 'Checo',
      da: 'Danés',
      de: 'Alemán',
      de_DE: 'Alemán (Alemania)',
      de_AT: 'Alemán (Austria)',
      el: 'Griego',
      en: 'Inglés',
      en_GB: 'Inglés (Reino Unido)',
      en_US: 'Inglés (Estados Unidos de América)',
      es: 'Español',
      es_ES: 'Español (España)',
      es_MX: 'Español (México)',
      es_US: 'Español (Estados Unidos de América)',
      et: 'Estonio',
      eu: 'Vasco',
      fi: 'Finlandés',
      fr: 'Francés',
      fr_FR: 'Francés (Francia)',
      fr_BE: 'Francés (Bélgica)',
      gl: 'Gallego',
      he: 'Hebreo',
      hr: 'Croata',
      hu: 'Húngaro',
      id: 'Indonesio',
      it: 'Italiano',
      ja: 'Japonés',
      ko: 'Coreano',
      lt: 'Lituano',
      lv: 'Letón',
      ms: 'Malayo',
      nl: 'Holandés',
      nl_NL: 'Holandés (Países Bajos)',
      nl_BE: 'Holandés (Bélgica)',
      no: 'Noruego',
      pl: 'Polaco',
      pt: 'Portugués',
      pt_BR: 'Portugués (Brasil)',
      pt_PT: 'Portugués (Portugal)',
      ro: 'Rumano',
      ru: 'Ruso',
      sk: 'Eslovaco',
      sl: 'Esloveno',
      sq: 'Albanés',
      sr: 'Serbio',
      sv: 'Sueco',
      th: 'Tailandés',
      tr: 'Turco',
      uk: 'Ucraniano',
      vi: 'Vietnamita',
      zh: 'Chino tradicional',
      zh_CN: 'Chino tradicional (China)',
      zh_TW: 'Chino tradicional (Taiwan)',
    },
    [FilterIdEnum.STORE]: {
      AD: 'Andorra',
      AE: 'Emiratos Árabes Unidos',
      AL: 'Albania',
      AM: 'Armenia',
      AT: 'Austria',
      AU: 'Australia',
      AW: 'Aruba',
      AZ: 'Azerbaiyán',
      BA: 'Bosnia y Herzegovina',
      BG: 'Bulgaria',
      BE: 'Bélgica',
      BH: 'Bahrein',
      BR: 'Brasil',
      BY: 'Bielorrusia',
      CA: 'Canadá',
      CH: 'Suiza',
      CL: 'Chile',
      CN: 'China',
      CO: 'Colombia',
      CR: 'Costa Rica',
      CY: 'Chipre',
      CZ: 'República Checa',
      DE: 'Alemania',
      DK: 'Dinamarca',
      DO: 'República Dominicana',
      DZ: 'Argelia',
      EC: 'Ecuador',
      EE: 'Estonia',
      EG: 'Egipto',
      ES: 'España',
      FI: 'Finlandia',
      FR: 'Francia',
      GB: 'Reino Unido',
      GE: 'Georgia',
      GR: 'Grecia',
      GT: 'Guatemala',
      HK: 'Hong Kong',
      HN: 'Honduras',
      HR: 'Croacia',
      HU: 'Hungría',
      IC: 'Islas Canarias',
      ID: 'Indonesia',
      IE: 'Irlanda',
      IL: 'Israel',
      IN: 'India',
      IQ: 'Iraq',
      IR: 'Irán',
      IS: 'Islandia',
      IT: 'Italia',
      JO: 'Jordán',
      JP: 'Japón',
      KR: 'Corea del Sur',
      KW: 'Kuwait',
      KZ: 'Kazajstán',
      LB: 'Líbano',
      LI: 'Liechtenstein',
      LK: 'Sri Lanka',
      LT: 'Lituania',
      LU: 'Luxemburgo',
      LV: 'Letonia',
      LY: 'Libia',
      MA: 'Marruecos',
      MC: 'Mónaco',
      ME: 'Montenegro',
      MK: 'Macedonia',
      MO: 'Macao',
      MQ: 'Martinica',
      MT: 'Malta',
      MX: 'México',
      MY: 'Malasia',
      NI: 'Nicaragua',
      NL: 'Países Bajos',
      NO: 'Noruega',
      NZ: 'Nueva Zelanda',
      OM: 'Omán',
      PA: 'Panamá',
      PH: 'Filipinas',
      PK: 'Pakistán',
      PL: 'Polonia',
      PR: 'Puerto Rico',
      PT: 'Portugal',
      QA: 'Qatar',
      RO: 'Rumanía',
      RS: 'Serbia',
      RU: 'Rusia',
      SA: 'Arabia Saudita',
      SE: 'Suecia',
      SG: 'Singapur',
      SI: 'Eslovenia',
      SK: 'Eslovaquia',
      SY: 'República Árabe Siria',
      SV: 'El Salvador',
      TH: 'Tailandia',
      TN: 'Túnez',
      TR: 'Turquía',
      TW: 'Taiwán',
      UA: 'Ucrania',
      US: 'Estados Unidos de América',
      VE: 'Venezuela',
      VN: 'Vietnam',
      WW: 'Todo el mundo',
      XK: 'Kosovo',
      ZA: 'Sudáfrica',
    },
    [FilterIdEnum.CATALOG]: {},
    [FilterIdEnum.COMPARE]: {
      [CompareFilterEnum.PREVIOUS_PERIOD]: 'Periodo anterior',
      [CompareFilterEnum.YEAR_OVER_YEAR]: 'Año tras año',
    },
    [FilterIdEnum.ACTIVE]: {
      [ActiveFilterEnum.ACTIVE]: 'Activo',
      [ActiveFilterEnum.INACTIVE]: 'Inactivo',
    },
    [FilterIdEnum.ENABLED]: {
      [EnabledFilterEnum.ENABLED]: 'Encendido',
      [EnabledFilterEnum.DISABLED]: 'Apagado',
    },
    [FilterIdEnum.AUDIT_OBJECT_TYPE]: {
      [AuditObjectTypeEnum.EQUALIZE]: APP_MESSAGES_ES.features[FeatureIdEnum.EQUALIZE],
      [AuditObjectTypeEnum.BLACKLIST]: APP_MESSAGES_ES.features[FeatureIdEnum.BLACKLIST],
      [AuditObjectTypeEnum.PROMOTION]: APP_MESSAGES_ES.features[FeatureIdEnum.PROMOTION],
      [AuditObjectTypeEnum.REDIRECTION]: APP_MESSAGES_ES.features[FeatureIdEnum.REDIRECTION],
      [AuditObjectTypeEnum.SYNONYM]: APP_MESSAGES_ES.features[FeatureIdEnum.SYNONYM],
      [AuditObjectTypeEnum.BROWSE_CONFIGURATION]:
        APP_MESSAGES_ES.features[FeatureIdEnum.CONFIGURATION][DomainEnum.BROWSE],
      [AuditObjectTypeEnum.SEARCH_CONFIGURATION]:
        APP_MESSAGES_ES.features[FeatureIdEnum.CONFIGURATION][DomainEnum.SEARCH],
      [AuditObjectTypeEnum.RELATED_TAGS]: APP_MESSAGES_ES.features[FeatureIdEnum.RELATED_TAGS],
      [AuditObjectTypeEnum.NEXT_QUERIES]: APP_MESSAGES_ES.features[FeatureIdEnum.NEXT_QUERIES],
      [AuditObjectTypeEnum.EXPLAIN]: APP_MESSAGES_ES.features[FeatureIdEnum.EXPLAIN],
      [AuditTypeEnum.CONFIG]: APP_MESSAGES_ES.features[FeatureIdEnum.INSTANCE_MANAGEMENT],
      [AuditObjectTypeEnum.CLIENT_CONFIG]: APP_MESSAGES_ES.features[FeatureIdEnum.CLIENT_CONFIG],
      [AuditObjectTypeEnum.ENDPOINT_CONFIG]:
        APP_MESSAGES_ES.features[FeatureIdEnum.ENDPOINT_CONFIG],
      [AuditObjectTypeEnum.FEATURE_CONFIG]: APP_MESSAGES_ES.features[FeatureIdEnum.FEATURE_CONFIG],
      [AuditObjectTypeEnum.INDEX_CLIENT]: APP_MESSAGES_ES.features[FeatureIdEnum.INDEX_CLIENT],
      [AuditObjectTypeEnum.INDEX_FEED_CONFIGURATION]:
        APP_MESSAGES_ES.features[FeatureIdEnum.INDEX_FEED_CONFIGURATION],
      [AuditObjectTypeEnum.INDEX_SCHEMA_CONFIGURATION]:
        APP_MESSAGES_ES.features[FeatureIdEnum.INDEX_SCHEMA_CONFIGURATION],
    },
  },
  customDate: {
    title: 'Selecciona un rango de fechas',
    startDate: 'Desde',
    endDate: 'Hasta',
    error: '',
  },
  errors: {
    [FilterIdEnum.DATE]: {
      minDate: 'La fecha no puede ser anterior a {date}',
      maxDate: 'La fecha no puede ser posterior a {date}',
      maxPeriod: 'La diferencia entre las dos fechas no puede ser mayor de {period} días',
      required: 'La fecha es obligatoria',
    },
  },
  placeholder: {
    single: 'Selecciona un valor',
    multiple: 'Selecciona valores',
  },
  search: 'Buscar',
  noResults: 'Sin resultados',
  useValue: 'Usar "{value}"',
}

export const FILTERS_MESSAGES: Dictionary<{ filters: FiltersMessages }> = {
  [LocaleEnum.ENGLISH]: {
    filters: FILTERS_MESSAGES_EN,
  },
  [LocaleEnum.SPANISH]: {
    filters: FILTERS_MESSAGES_ES,
  },
}
